import { Injectable, OnDestroy } from '@angular/core';
import { LoggingService } from '@app/core/services/logging/logging.service';
import { Geolocation } from '@capacitor/geolocation';
import { Platform } from '@ionic/angular/standalone';
import { WaGeolocationService as NgGeolocationService } from '@ng-web-apis/geolocation';
import { WaPermissionsService } from '@ng-web-apis/permissions';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

export interface LocationPosition {
  lat: number;
  lng: number;
  accuracy?: number;
  altitude?: number;
  altitudeAccuracy?: number;
  heading?: number;
  speed?: number;
  timestamp?: number;
}

export interface GeolocationState {
  isPermissionGranted: boolean;
  isLocationEnabled: boolean;
  lastKnownPosition: LocationPosition | null;
  isWatching: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class GeolocationService implements OnDestroy {
  private _state$ = new BehaviorSubject<GeolocationState>({
    isPermissionGranted: false,
    isLocationEnabled: false,
    lastKnownPosition: null,
    isWatching: false,
  });

  private _currentPosition$ = new BehaviorSubject<LocationPosition | null>(
    null,
  );
  private watchId: string | null = null;

  public readonly state$ = this._state$.asObservable();
  public readonly currentPosition$ = this._currentPosition$.asObservable();

  constructor(
    private platform: Platform,
    private ngGeolocationService: NgGeolocationService,
    private permissionsService: WaPermissionsService,
  ) {
    this.initializeService();
  }

  private async initializeService(): Promise<void> {
    // Check initial permission state
    const hasPermission = await this.checkPermissions();
    this.updateState({ isPermissionGranted: hasPermission });

    if (hasPermission) {
      // Try to get last known position
      await this.getLastKnownPosition();
    }
  }

  private updateState(updates: Partial<GeolocationState>): void {
    const currentState = this._state$.value;
    this._state$.next({ ...currentState, ...updates });
  }

  private handleGeolocationError(error: GeolocationPositionError): {
    error: string;
    message: string;
  } {
    switch (error.code) {
      case 1: {
        // PERMISSION_DENIED
        return {
          error: 'Permission Denied',
          message: 'Please allow location access in your device settings.',
        };
      }
      case 2: {
        // POSITION_UNAVAILABLE
        return {
          error: 'Location Unavailable',
          message:
            "Your location could not be determined. Please check your device's location services and try again.",
        };
      }
      case 3: {
        // TIMEOUT
        return {
          error: 'Timeout',
          message:
            'The request to get your location timed out. Please try again.',
        };
      }
      default: {
        return {
          error: 'Unknown Error',
          message: 'An unknown error occurred while getting your location.',
        };
      }
    }
  }

  public async checkPermissions(): Promise<boolean> {
    if (this.platform.is('desktop') || this.platform.is('mobileweb')) {
      try {
        const permissionStatus = await firstValueFrom(
          this.permissionsService.state('geolocation'),
        );
        return permissionStatus === 'granted';
      } catch (error) {
        LoggingService.error('Permission check error:', error);
        return false;
      }
    }

    try {
      const permStatus = await Geolocation.checkPermissions();
      return permStatus.location === 'granted';
    } catch (error) {
      LoggingService.error('Permission check error:', error);
      return false;
    }
  }

  public async requestPermissions(): Promise<boolean> {
    LoggingService.log('🔍 Platform check:', {
      isDesktop: this.platform.is('desktop'),
      isMobileWeb: this.platform.is('mobileweb'),
      isIOS: this.platform.is('ios'),
      isAndroid: this.platform.is('android'),
      platforms: this.platform.platforms(),
    });

    if (this.platform.is('desktop') || this.platform.is('mobileweb')) {
      try {
        LoggingService.log('Checking geolocation permission state...');
        const permissionStatus = await firstValueFrom(
          this.permissionsService.state('geolocation'),
        );
        LoggingService.log('Current permission status:', permissionStatus);

        if (permissionStatus === 'prompt') {
          LoggingService.log(
            'Permission is in prompt state, attempting to get position...',
          );
          return new Promise((resolve) => {
            if (!navigator.geolocation) {
              LoggingService.error(
                'Geolocation is not supported by this browser',
              );
              resolve(false);
              return;
            }

            try {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  LoggingService.log('Successfully got position:', position);
                  this.updateState({ isPermissionGranted: true });
                  resolve(true);
                },
                (error) => {
                  const errorInfo = this.handleGeolocationError(error);
                  LoggingService.error(
                    'Geolocation permission error:',
                    errorInfo,
                  );
                  this.updateState({ isPermissionGranted: false });
                  resolve(false);
                },
                { enableHighAccuracy: true, timeout: 10_000 },
              );
            } catch (error) {
              LoggingService.error('Geolocation permission error:', error);
              this.updateState({ isPermissionGranted: false });
              resolve(false);
            }
          });
        }

        const granted = permissionStatus === 'granted';
        this.updateState({ isPermissionGranted: granted });
        return granted;
      } catch (error) {
        LoggingService.error('Permission error:', error);
        this.updateState({ isPermissionGranted: false });
        return false;
      }
    }

    try {
      LoggingService.log('Requesting permissions for mobile platform...');

      // Add timeout for permission request
      const permissionPromise = Geolocation.requestPermissions();
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error('Permission request timeout'));
        }, 10_000);
      });

      const permStatus = await Promise.race([
        permissionPromise,
        timeoutPromise,
      ]);
      LoggingService.log('Mobile permission status:', permStatus);
      const granted = permStatus.location === 'granted';
      this.updateState({ isPermissionGranted: granted });
      return granted;
    } catch (error) {
      LoggingService.error('Permission error:', error);

      // For iOS Simulator, sometimes permission requests fail but we can still try to get location
      if (this.platform.is('ios') && this.isSimulatorEnvironment()) {
        LoggingService.log(
          'iOS Simulator detected, assuming permission granted for testing',
        );
        this.updateState({ isPermissionGranted: true });
        return true;
      }

      this.updateState({ isPermissionGranted: false });
      return false;
    }
  }

  public async getCurrentPosition(options?: {
    enableHighAccuracy?: boolean;
    timeout?: number;
    maximumAge?: number;
  }): Promise<LocationPosition | null> {
    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10_000,
      maximumAge: 0,
      ...options,
    };

    if (this.platform.is('desktop') || this.platform.is('mobileweb')) {
      try {
        LoggingService.log('Getting current position for web platform...');
        const permissionStatus = await firstValueFrom(
          this.permissionsService.state('geolocation'),
        );
        LoggingService.log('Current permission status:', permissionStatus);

        if (permissionStatus === 'prompt') {
          LoggingService.log(
            'Permission is in prompt state, attempting to get position...',
          );
          return new Promise((resolve) => {
            if (!navigator.geolocation) {
              LoggingService.error(
                'Geolocation is not supported by this browser',
              );
              resolve(null);
              return;
            }

            navigator.geolocation.getCurrentPosition(
              (position) => {
                LoggingService.log('Successfully got position:', position);
                const locationPosition: LocationPosition = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                  accuracy: position.coords.accuracy,
                  altitude: position.coords.altitude ?? undefined,
                  altitudeAccuracy:
                    position.coords.altitudeAccuracy ?? undefined,
                  heading: position.coords.heading ?? undefined,
                  speed: position.coords.speed ?? undefined,
                  timestamp: position.timestamp,
                };
                this.updateState({ lastKnownPosition: locationPosition });
                this._currentPosition$.next(locationPosition);
                resolve(locationPosition);
              },
              (error) => {
                const errorInfo = this.handleGeolocationError(error);
                LoggingService.error('Geolocation error:', errorInfo);
                resolve(null);
              },
              defaultOptions,
            );
          });
        } else if (permissionStatus !== 'granted') {
          LoggingService.error('Geolocation permission denied');
          return null;
        }

        LoggingService.log('Permission is granted, getting position...');
        return new Promise((resolve) => {
          if (!navigator.geolocation) {
            LoggingService.error(
              'Geolocation is not supported by this browser',
            );
            resolve(null);
            return;
          }

          navigator.geolocation.getCurrentPosition(
            (position) => {
              LoggingService.log('Successfully got position:', position);
              const locationPosition: LocationPosition = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude ?? undefined,
                altitudeAccuracy: position.coords.altitudeAccuracy ?? undefined,
                heading: position.coords.heading ?? undefined,
                speed: position.coords.speed ?? undefined,
                timestamp: position.timestamp,
              };
              this.updateState({ lastKnownPosition: locationPosition });
              this._currentPosition$.next(locationPosition);
              resolve(locationPosition);
            },
            (error) => {
              const errorInfo = this.handleGeolocationError(error);
              LoggingService.error('Geolocation error:', errorInfo);
              resolve(null);
            },
            defaultOptions,
          );
        });
      } catch (error) {
        LoggingService.error('Permission error:', error);
        return null;
      }
    }

    try {
      LoggingService.log('Getting current position for mobile platform...');
      const granted = await this.requestPermissions();
      if (!granted) {
        LoggingService.error('Geolocation permission denied');
        return null;
      }

      // Add timeout wrapper for Capacitor geolocation
      const positionPromise = Geolocation.getCurrentPosition({
        enableHighAccuracy: defaultOptions.enableHighAccuracy,
        timeout: defaultOptions.timeout,
      });

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(
            new Error(`Geolocation timeout after ${defaultOptions.timeout}ms`),
          );
        }, defaultOptions.timeout + 1000); // Add extra buffer
      });

      const position = await Promise.race([positionPromise, timeoutPromise]);

      if (!position || !position.coords) {
        LoggingService.error('Invalid position data from Capacitor');
        // For iOS Simulator, try to return a mock position if no real position is available
        if (this.platform.is('ios') && this.isSimulatorEnvironment()) {
          LoggingService.log('Returning mock position for iOS Simulator');
          const mockPosition: LocationPosition = {
            lat: 37.7749, // San Francisco coordinates as fallback
            lng: -122.4194,
            accuracy: 100,
            timestamp: Date.now(),
          };
          this.updateState({ lastKnownPosition: mockPosition });
          this._currentPosition$.next(mockPosition);
          return mockPosition;
        }
        return null;
      }

      LoggingService.log('Successfully got position from Capacitor:', position);
      const locationPosition: LocationPosition = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
        altitude: position.coords.altitude ?? undefined,
        altitudeAccuracy: position.coords.altitudeAccuracy ?? undefined,
        heading: position.coords.heading ?? undefined,
        speed: position.coords.speed ?? undefined,
        timestamp: position.timestamp,
      };

      this.updateState({ lastKnownPosition: locationPosition });
      this._currentPosition$.next(locationPosition);
      return locationPosition;
    } catch (error) {
      LoggingService.error('Geolocation error:', error);

      // For iOS Simulator, provide a fallback mock position
      if (this.platform.is('ios') && this.isSimulatorEnvironment()) {
        LoggingService.log(
          'Providing fallback mock position for iOS Simulator due to error',
        );
        const mockPosition: LocationPosition = {
          lat: 37.7749, // San Francisco coordinates as fallback
          lng: -122.4194,
          accuracy: 100,
          timestamp: Date.now(),
        };
        this.updateState({ lastKnownPosition: mockPosition });
        this._currentPosition$.next(mockPosition);
        return mockPosition;
      }

      return null;
    }
  }

  public async watchPosition(options?: {
    enableHighAccuracy?: boolean;
    timeout?: number;
    maximumAge?: number;
  }): Promise<boolean> {
    if (this.watchId) {
      LoggingService.log('Already watching position');
      return true;
    }

    const defaultOptions = {
      enableHighAccuracy: true,
      timeout: 10_000,
      maximumAge: 5000,
      ...options,
    };

    try {
      const granted = await this.requestPermissions();
      if (!granted) {
        LoggingService.error('Geolocation permission denied for watching');
        return false;
      }

      if (this.platform.is('desktop') || this.platform.is('mobileweb')) {
        if (!navigator.geolocation) {
          LoggingService.error('Geolocation is not supported by this browser');
          return false;
        }

        this.watchId = navigator.geolocation
          .watchPosition(
            (position) => {
              LoggingService.log('Position update:', position);
              const locationPosition: LocationPosition = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude ?? undefined,
                altitudeAccuracy: position.coords.altitudeAccuracy ?? undefined,
                heading: position.coords.heading ?? undefined,
                speed: position.coords.speed ?? undefined,
                timestamp: position.timestamp,
              };
              this.updateState({ lastKnownPosition: locationPosition });
              this._currentPosition$.next(locationPosition);
            },
            (error) => {
              const errorInfo = this.handleGeolocationError(error);
              LoggingService.error('Watch position error:', errorInfo);
            },
            defaultOptions,
          )
          .toString();
      } else {
        this.watchId = await Geolocation.watchPosition(
          {
            enableHighAccuracy: defaultOptions.enableHighAccuracy,
            timeout: defaultOptions.timeout,
          },
          (position, err) => {
            if (err) {
              LoggingService.error('Watch position error:', err);
              return;
            }

            if (position && position.coords) {
              LoggingService.log('Position update from Capacitor:', position);
              const locationPosition: LocationPosition = {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude ?? undefined,
                altitudeAccuracy: position.coords.altitudeAccuracy ?? undefined,
                heading: position.coords.heading ?? undefined,
                speed: position.coords.speed ?? undefined,
                timestamp: position.timestamp,
              };
              this.updateState({ lastKnownPosition: locationPosition });
              this._currentPosition$.next(locationPosition);
            }
          },
        );
      }

      this.updateState({ isWatching: true });
      LoggingService.log('Started watching position with ID:', this.watchId);
      return true;
    } catch (error) {
      LoggingService.error('Error starting watch position:', error);
      return false;
    }
  }

  public async clearWatch(): Promise<void> {
    if (!this.watchId) {
      LoggingService.log('No active watch to clear');
      return;
    }

    try {
      if (this.platform.is('desktop') || this.platform.is('mobileweb')) {
        navigator.geolocation.clearWatch(Number.parseInt(this.watchId));
      } else {
        await Geolocation.clearWatch({ id: this.watchId });
      }

      LoggingService.log('Cleared watch position with ID:', this.watchId);
      this.watchId = null;
      this.updateState({ isWatching: false });
    } catch (error) {
      LoggingService.error('Error clearing watch position:', error);
    }
  }

  public async getLastKnownPosition(): Promise<LocationPosition | null> {
    if (this.platform.is('desktop') || this.platform.is('mobileweb')) {
      // For web, we return the last known position from state
      return this._state$.value.lastKnownPosition;
    }

    try {
      // For native platforms, we can get the last known position from the system
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 5000,
      });

      if (position && position.coords) {
        const locationPosition: LocationPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude ?? undefined,
          altitudeAccuracy: position.coords.altitudeAccuracy ?? undefined,
          heading: position.coords.heading ?? undefined,
          speed: position.coords.speed ?? undefined,
          timestamp: position.timestamp,
        };

        this.updateState({ lastKnownPosition: locationPosition });
        return locationPosition;
      }
    } catch (error) {
      LoggingService.log('Could not get last known position:', error);
    }

    return this._state$.value.lastKnownPosition;
  }

  public isLocationEnabled(): boolean {
    return this._state$.value.isLocationEnabled;
  }

  public hasPermission(): boolean {
    return this._state$.value.isPermissionGranted;
  }

  public getCurrentState(): GeolocationState {
    return this._state$.value;
  }

  private isSimulatorEnvironment(): boolean {
    // Check if we're running in iOS Simulator
    // This is a heuristic - in simulator, certain device properties are different
    return (
      this.platform.is('ios') &&
      ((window as any).webkit === undefined ||
        (window as any).DeviceMotionEvent === undefined ||
        navigator.userAgent.includes('Simulator'))
    );
  }

  public async openLocationSettings(): Promise<void> {
    if (this.platform.is('desktop') || this.platform.is('mobileweb')) {
      // For web, we can't open system settings, but we can show instructions
      LoggingService.log(
        'Please enable location services in your browser settings',
      );
      return;
    }

    try {
      // For native platforms, we could implement opening system settings
      // This would require additional plugins like @capacitor/preferences or native settings
      LoggingService.log('Opening location settings...');
      // Implementation would depend on additional plugins
    } catch (error) {
      LoggingService.error('Error opening location settings:', error);
    }
  }

  public ngOnDestroy(): void {
    this.clearWatch();
  }
}
